// src/services/purchaseBillService.js
// SOT: purchase_bills (the vendor's bill for one goods receipt) - D-291 P3d
// SOT: financial_transactions (the payable pbill_{billId}, created ONLY here)
// Constitution: ALL business logic through service layer
// Constitution: UI MUST NEVER write Firestore directly
//
// WHY THIS FILE EXISTS
// Goods and bill are two events (D-291, Ravi 21 Oct). The GRN puts the goods
// in stock and leaves the receipt "awaiting_bill"; THIS file turns the
// vendor's bill into what we owe the vendor. It is the ONLY writer of
// purchase_bills, purchase_bill_numbers and purchase-bill payables - the
// Firestore rules (P3d-2, tests/rules/d291.rules.test.mjs) refuse any other
// shape.
//
// Two events, each ONE transaction (a second tab, a retry or a double click
// is refused inside the transaction - never written twice):
//
//   createPurchaseBill()   bill + payable + receipt "billed" (+ invoice key)
//     - re-reads receipt, PO, vendor and company; company checked on each
//     - the receipt must still be "awaiting_bill" with no bill
//     - planBill() (src/utils/billPlan.js) runs again HERE with what was
//       read: CGST / SGST / IGST, round off and total are always computed,
//       never taken from the screen; refused when ok is false
//     - a tax invoice: the vendor's invoice no must be new for this vendor
//       (purchase_bill_numbers/{company}_{vendor}_{key}, read and created in
//       the same transaction - no check-then-write race)
//
//   upgradeProformaBill()  proforma -> tax invoice (+ invoice key)
//     - accounts work (owner / accounts, or "Accounts access" - D-314)
//     - refused when the bill changed since the page loaded it (updated_at)
//     - only the invoice fields change; the amount never does, and the
//       payable row is never touched (the khaata reads the bill type from
//       the bill doc - src/utils/khaata.js)

import {
  collection, doc, runTransaction, serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { planBillFor, checkInvoiceFields } from "../utils/billPlan";
import { validateGstin, vendorGstinOf, companyClaimsItc } from "../utils/gstBill";

// Who may enter / upgrade a bill (Ravi, 21 Oct) - same lists as firestore.rules.
export const BILL_ENTRY_ROLES = ["owner", "accounts", "sm", "manager", "store"];
export const ACCOUNTS_ROLES = ["owner", "accounts"];
// D-314: the roles the Owner may give "Accounts access" - same list as
// syncUserClaims (functions repo) and canDoAccounts() in firestore.rules.
export const ACCOUNTS_ACCESS_ROLES = ["manager", "sm"];

export const canEnterBills = (user) => BILL_ENTRY_ROLES.includes(user?.role);
// D-314: owner / accounts, or a manager / sm the Owner gave "Accounts access".
// Mirrors canDoAccounts() in firestore.rules - a stray flag on another role does nothing.
// The ONE client check: menus, routes, Payables, Opening Balances and the proforma upgrade.
export const canDoAccounts = (user) =>
  ACCOUNTS_ROLES.includes(user?.role) ||
  (ACCOUNTS_ACCESS_ROLES.includes(user?.role) && user?.accounts_access === true);

const userName = (user) => user?.name || user?.displayName || user?.email || "Unknown";

// Today in India as "YYYY-MM-DD" - the invoice date cannot be after it.
export const todayIST = (now = new Date()) =>
  new Date(now.getTime() + 330 * 60 * 1000).toISOString().slice(0, 10);

// Same Firestore Timestamp? (seconds + nanoseconds; both missing = same)
const sameStamp = (a, b) => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return Number(a.seconds) === Number(b.seconds) && Number(a.nanoseconds) === Number(b.nanoseconds);
};

const keyDocId = (companyId, vendorId, key) => `${companyId}_${vendorId}_${key}`;

/**
 * Enter the vendor's bill for ONE receipt.
 * @param {Object} a
 * @param {string} a.company_id
 * @param {string} a.receipt_id
 * @param {Object} a.bill   what was typed: { bill_type, vendor_invoice_no,
 *   vendor_invoice_date, lines [{ line_id, billed_qty, base_rate, gst_percent,
 *   hsn_code }], charges [{ description, amount, gst_percent, hsn_code }],
 *   round_off, printed_total, supply_type (used ONLY when a GSTIN is missing
 *   and the user chose same / other state), stock_factor_by_line }
 * @param {Object} a.user   { uid, role, name, email }
 * @returns {{ bill_id, payable_id, bill_total, bill_type, warnings }}
 */
export async function createPurchaseBill({ company_id, receipt_id, bill = {}, user, now } = {}) {
  const companyId = String(company_id || "").trim();
  const receiptId = String(receipt_id || "").trim();
  if (!companyId) throw new Error("company_id required");
  if (!receiptId) throw new Error("Receipt missing");
  if (!user?.uid) throw new Error("Your login could not be read. Please log in again.");
  if (!canEnterBills(user)) throw new Error("You do not have permission to enter purchase bills.");

  const receiptRef = doc(db, "purchase_receipts", receiptId);
  const billRef    = doc(collection(db, "purchase_bills"));
  const payableId  = `pbill_${billRef.id}`;
  const payableRef = doc(db, "financial_transactions", payableId);
  const today      = todayIST(now);
  let result = null;

  await runTransaction(db, async (tx) => {
    // ── 1. reads (all before any write) ──
    const rcSnap = await tx.get(receiptRef);
    if (!rcSnap.exists()) throw new Error("Receipt not found");
    const rc = rcSnap.data();
    if (rc.company_id !== companyId) throw new Error("Access denied - receipt of another company");
    if (rc.purchase_bill_id || rc.bill_status === "billed") {
      throw new Error("A bill was already entered for this receipt - nothing was saved. Refresh the page.");
    }
    if (rc.bill_status !== "awaiting_bill") {
      throw new Error("This receipt is from before purchase bills - its vendor bill is already in Payables. Nothing was saved.");
    }
    const vendorId = String(rc.vendor_id || "").trim();
    if (!vendorId) throw new Error("This receipt has no vendor - it cannot be billed.");

    const poSnap = rc.purchase_order_id ? await tx.get(doc(db, "purchase_orders", rc.purchase_order_id)) : null;
    if (!poSnap || !poSnap.exists()) throw new Error("Purchase order of this receipt not found");
    const po = poSnap.data();
    if (po.company_id !== companyId) throw new Error("Access denied - purchase order of another company");
    // P3d-5a: a Direct Site finalize that stopped part-way leaves the PO
    // "finalizing" (admin must check site stock first) - its receipt may
    // already exist, but no bill until the finalize is completed.
    if (po.purchase_mode === "direct_site_purchase" && po.site_confirmation_status !== "completed") {
      throw new Error("This site purchase did not finish finalizing - its bill cannot be entered yet. Ask admin to check the PO first. Nothing was saved.");
    }

    const vSnap = await tx.get(doc(db, "vendors", vendorId));
    if (!vSnap.exists()) throw new Error("Vendor not found");
    const vendor = vSnap.data();
    if (vendor.company_id !== companyId) throw new Error("Access denied - vendor of another company");

    const cSnap = await tx.get(doc(db, "companies", companyId));
    const company = cSnap.exists() ? cSnap.data() : {};

    // ── 2. the bill maths, again, with what was read here ──
    // P3d-5a: planBillFor() is the SAME function the bill page runs on every
    // keystroke: same / other state from the two GSTINs (the screen's choice
    // only when they cannot decide), GST credit only with a valid vendor
    // GSTIN, a proforma saves no invoice no / date. Never totals from the page.
    const plan = planBillFor({
      receiptItems: Array.isArray(rc.items) ? rc.items : [],
      company,
      vendor,
      bill,
      today,
    });
    if (!plan.ok) throw new Error(`Please fix the bill:\n- ${plan.errors.join("\n- ")}`);
    if (!(plan.bill_total > 0)) throw new Error("The bill total must be more than 0.");

    // ── 3. duplicate vendor invoice no (tax invoice) - read in THIS transaction ──
    let keyRef = null;
    if (plan.bill_type === "tax_invoice") {
      keyRef = doc(db, "purchase_bill_numbers", keyDocId(companyId, vendorId, plan.invoice_key));
      const keySnap = await tx.get(keyRef);
      if (keySnap.exists()) {
        throw new Error(`Invoice no ${plan.invoice_no} of this vendor is already entered - nothing was saved.`);
      }
    }

    // ── 4. writes ──
    const vendorName = vendor.vendor_name || vendor.name || rc.vendor_name || "";
    tx.set(billRef, {
      company_id:           companyId,
      vendor_id:            vendorId,
      vendor_name:          vendorName,
      vendor_gstin:         plan.vendor_gstin,          // P3d-5a-6: as printed on the bill
      vendor_master_gstin:  plan.vendor_master_gstin,   // Vendor Master when the bill was entered
      purchase_order_id:    rc.purchase_order_id,
      po_number:            po.po_number || rc.po_number || "",
      project_id:           po.project_id || "",
      project_name:         po.project_name || "",
      purchase_receipt_ids: [receiptId],
      bill_type:            plan.bill_type,
      vendor_invoice_no:    plan.invoice_no,
      invoice_key:          plan.invoice_key,
      vendor_invoice_date:  plan.invoice_date,
      supply_type:          plan.supply_type,
      lines:                plan.lines,
      charges:              plan.charges,
      items_taxable:        plan.items_taxable,
      charges_taxable:      plan.charges_taxable,
      taxable_total:        plan.taxable_total,
      cgst:                 plan.cgst,
      sgst:                 plan.sgst,
      igst:                 plan.igst,
      tax_total:            plan.tax_total,
      round_off:            plan.round_off,
      bill_total:           plan.bill_total,
      printed_total:        plan.printed_total,
      itc_eligible:         plan.itc_eligible,
      itc_amount:           plan.itc_amount,
      stock_value:          plan.stock_value,
      gst_in_stock_cost:    plan.gst_in_stock_cost,
      payable_id:           payableId,
      created_by:           user.uid,
      created_by_name:      userName(user),
      created_at:           serverTimestamp(),
      updated_at:           serverTimestamp(),
    });

    // The payable - no status / paid_amount / balance: the khaata works out
    // Paid / Partly / Due from payments (src/utils/khaata.js).
    tx.set(payableRef, {
      company_id:          companyId,
      type:                "vendor",
      sub_type:            "purchase_bill",
      category:            "material",
      reference_type:      "purchase_bill",
      reference_id:        billRef.id,
      purchase_bill_id:    billRef.id,
      bill_type:           plan.bill_type,
      vendor_invoice_no:   plan.invoice_no,
      vendor_id:           vendorId,
      vendor_name:         vendorName,
      purchase_order_id:   rc.purchase_order_id,
      po_number:           po.po_number || rc.po_number || "",
      purchase_receipt_id: receiptId,
      project_id:          po.project_id || "",
      project_name:        po.project_name || "",
      amount:              plan.bill_total,
      created_by:          user.uid,
      created_by_name:     userName(user),
      created_at:          serverTimestamp(),
    });

    tx.update(receiptRef, {
      bill_status:      "billed",
      purchase_bill_id: billRef.id,
    });

    if (keyRef) {
      tx.set(keyRef, {
        company_id:       companyId,
        vendor_id:        vendorId,
        invoice_key:      plan.invoice_key,
        vendor_invoice_no: plan.invoice_no,
        purchase_bill_id: billRef.id,
        created_by:       user.uid,
        created_at:       serverTimestamp(),
      });
    }

    // P3d-5a-6: Vendor Master had no / an invalid GSTIN (or "Unregistered")
    // and the bill shows a valid one -> saved there too, in this same
    // transaction, so every future PO and bill uses it. Never written over a
    // DIFFERENT valid GSTIN (that is only warned). Staff can untick it.
    const masterUpdated = plan.vendor_master_fix === true && bill.update_vendor_master !== false;
    if (masterUpdated) {
      tx.update(doc(db, "vendors", vendorId), {
        gst_number:               plan.vendor_gstin,
        gst_registered:           true,
        gst_updated_from_bill_id: billRef.id,
        gst_updated_by:           user.uid,
        gst_updated_by_name:      userName(user),
        updated_at:               serverTimestamp(),
      });
    }

    result = {
      bill_id:    billRef.id,
      payable_id: payableId,
      bill_total: plan.bill_total,
      bill_type:  plan.bill_type,
      warnings:   plan.warnings,
      vendor_master_updated: masterUpdated,
    };
  });

  return result;
}

/**
 * A proforma becomes the tax invoice (accounts work). Only the invoice
 * fields change; the amount, lines and payable never do.
 * @param {Object} a
 * @param {string} a.company_id
 * @param {string} a.bill_id
 * @param {Object} a.loaded_updated_at   bill.updated_at as the page loaded it
 * @param {string} a.vendor_invoice_no
 * @param {string} a.vendor_invoice_date "YYYY-MM-DD"
 * @param {Object} a.user   { uid, role, name, email, accounts_access }
 * @returns {{ bill_id, invoice_no, itc_amount }}
 */
export async function upgradeProformaBill({ company_id, bill_id, loaded_updated_at, vendor_invoice_no, vendor_invoice_date, user, now } = {}) {
  const companyId = String(company_id || "").trim();
  const billId = String(bill_id || "").trim();
  if (!companyId) throw new Error("company_id required");
  if (!billId) throw new Error("Bill missing");
  if (!user?.uid) throw new Error("Your login could not be read. Please log in again.");
  if (!canDoAccounts(user)) throw new Error("Only Owner or Accounts can turn a proforma into a tax invoice.");

  const inv = checkInvoiceFields({
    billType: "tax_invoice", invoiceNo: vendor_invoice_no, invoiceDate: vendor_invoice_date, today: todayIST(now),
  });
  if (inv.errors.length > 0) throw new Error(`Please fix:\n- ${inv.errors.join("\n- ")}`);

  const billRef = doc(db, "purchase_bills", billId);
  let result = null;

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(billRef);
    if (!snap.exists()) throw new Error("Bill not found");
    const bill = snap.data();
    if (bill.company_id !== companyId) throw new Error("Access denied - bill of another company");
    if (bill.bill_type !== "proforma") throw new Error("This bill is already a tax invoice - nothing was saved.");
    if (!sameStamp(bill.updated_at, loaded_updated_at)) {
      throw new Error("This bill was changed by someone else after you opened it - nothing was saved. Refresh the page and try again.");
    }

    const keyRef = doc(db, "purchase_bill_numbers", keyDocId(companyId, bill.vendor_id, inv.invoice_key));
    const keySnap = await tx.get(keyRef);
    if (keySnap.exists()) {
      throw new Error(`Invoice no ${inv.invoice_no} of this vendor is already entered - nothing was saved.`);
    }
    const cSnap = await tx.get(doc(db, "companies", companyId));
    const company = cSnap.exists() ? cSnap.data() : {};

    // The vendor is read here for the company check, and for its GSTIN when an old proforma has no vendor_gstin (P3d-5b-3).
    const vSnap = bill.vendor_id ? await tx.get(doc(db, "vendors", bill.vendor_id)) : null;
    const vendor = vSnap && vSnap.exists() ? vSnap.data() : null;
    if (vendor && vendor.company_id !== companyId) throw new Error("Access denied - vendor of another company");

    // P3d-5b-3: the GSTIN PRINTED on the bill decides (saved as vendor_gstin at
    // entry, P3d-5a-6) - not Vendor Master today. An old proforma saved before
    // 5a-6 has no vendor_gstin -> Vendor Master (read above) decides.
    const printedGstin = bill.vendor_gstin !== undefined ? bill.vendor_gstin : vendorGstinOf(vendor || {});
    // GST credit now that it is a tax invoice: company claims ITC, the bill's GSTIN
    // valid, not unregistered, and the stock was costed before GST when the
    // proforma was entered (else the GST is already in the stock value).
    const itcEligible = companyClaimsItc(company)
      && validateGstin(printedGstin).ok
      && bill.supply_type !== "unregistered"
      && bill.gst_in_stock_cost !== true;
    const itcAmount = itcEligible ? Number(bill.tax_total || 0) : 0;

    tx.update(billRef, {
      bill_type:           "tax_invoice",
      vendor_invoice_no:   inv.invoice_no,
      invoice_key:         inv.invoice_key,
      vendor_invoice_date: inv.invoice_date,
      itc_eligible:        itcEligible,
      itc_amount:          itcAmount,
      upgraded_by:         user.uid,
      upgraded_by_name:    userName(user),
      upgraded_at:         serverTimestamp(),
      updated_at:          serverTimestamp(),
    });
    tx.set(keyRef, {
      company_id:        companyId,
      vendor_id:         bill.vendor_id,
      invoice_key:       inv.invoice_key,
      vendor_invoice_no: inv.invoice_no,
      purchase_bill_id:  billId,
      created_by:        user.uid,
      created_at:        serverTimestamp(),
    });

    result = { bill_id: billId, invoice_no: inv.invoice_no, itc_amount: itcAmount };
  });

  return result;
}